import { getAuthorized } from "@/lib/fetch";
import { Select, SelectItem } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldValues, Path, PathValue } from "react-hook-form";
import { SelectFieldProps } from "./types";

interface TranslationRecord {
  [key: string]: string;
}

interface TranslationResponse {
  [section: string]: TranslationRecord;
}

export function SelectField<T extends FieldValues>({
  field,
  setValue,
  watch,
}: {
  field: SelectFieldProps;
  setValue: (name: Path<T>, value: PathValue<T, Path<T>>) => void;
  watch: (name: Path<T>) => any;
}) {
  const { t } = useTranslation();
  const translatedLabel = t(field.label, {
    ns: "tables",
    defaultValue: t(field.label, { defaultValue: field.label }),
  });
  const currentValue = watch(field.name as Path<T>);
  const selectedValue = currentValue ? new Set([currentValue]) : new Set([]);
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  // Fetch translations if useTranslation is true
  const { data: translations, isLoading } = useQuery<TranslationResponse>({
    queryKey: [
      field.useTranslation ? field.table_name : "",
      "translations",
      field.useTranslation ? field.language : "fr",
    ],
    queryFn: async () => {
      if (!field.useTranslation || !field.client || !field.table_name) {
        return {};
      }

      const res = await getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${field.client}/${
          field.table_name
        }/translations?language=${field.language || "fr"}`,
      );

      if (!res.ok) {
        throw new Error("Failed to fetch translations");
      }

      return res.json();
    },
    enabled: field.useTranslation === true,
  });

  // Process options based on translations
  useEffect(() => {
    if (field.useTranslation && translations) {
      const section_key = field.translate_key || "";
      const translationSection = translations[section_key] || {};

      // Transform keys from translation to options
      const translatedOptions = Object.entries(translationSection)
        .map(([key, label]) => ({
          value: key,
          label: label,
        }))
        .sort((a, b) =>
          field.sortByLabel
            ? a.label.localeCompare(b.label, undefined, {
                sensitivity: "base",
              })
            : 0,
        );

      setOptions(translatedOptions);
    } else if (field.useTranslation === false && field.options) {
      // Use direct options if no translation or translation not loaded yet
      const directOptions = field.sortByLabel
        ? [...field.options].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, {
              sensitivity: "base",
            }),
          )
        : field.options;
      setOptions(directOptions);
    }
  }, [translations, field]);

  const translateOptionLabel = (label: string) =>
    field.useTranslation ? label : t(label, { defaultValue: label });

  return (
    <Select
      size="sm"
      labelPlacement="outside"
      key={field.name}
      label={translatedLabel}
      isLoading={field.useTranslation && isLoading}
      onChange={(e) => {
        const value = e.target.value;
        setValue(field.name as Path<T>, value as PathValue<T, Path<T>>);
      }}
      selectedKeys={selectedValue}
      classNames={{
        trigger:
          "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white border-2 shadow-sm",
        label: "text-blue-600 group-data-[filled=true]:text-blue-600",
      }}
    >
      {options.map((option) => {
        const label = translateOptionLabel(option.label);
        return (
          <SelectItem key={option.value} textValue={label}>
            {label}
          </SelectItem>
        );
      })}
    </Select>
  );
}
